import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { tenderSlug } from "@/lib/tenderSlug";
import { SeoHead } from "@/components/SeoHead";
import { useListTenders, useGetTenderStats, useListCategories, useGetRecentTenders, useGetClosingSoonTenders, useGetTodayTenders } from "@workspace/api-client-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useUser } from "@clerk/react";
import { TrendingUp, Clock, ChevronRight, FileText, UtensilsCrossed, Wrench, GraduationCap, Monitor, Bus, Shirt, Printer, Sparkles, Briefcase, MoreHorizontal, MapPin, Activity, School, RefreshCw, CalendarCheck, ArrowUpDown, Shield, Leaf } from "lucide-react";
import { SOCIAL_LINKS } from "@/components/layout/Footer";
import { PUBLIC_PLATFORM_INTRO } from "@workspace/public-seo";

const DISTRICTS = [
  "全港", "中西區", "灣仔區", "東區", "南區", "油尖旺區", "深水埗區", "九龍城區", "黃大仙區",
  "觀塘區", "葵青區", "荃灣區", "屯門區", "元朗區", "北區", "大埔區", "沙田區", "西貢區", "離島區"
];

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  catering: <UtensilsCrossed className="h-8 w-8" />,
  engineering: <Wrench className="h-8 w-8" />,
  "study-tour": <GraduationCap className="h-8 w-8" />,
  it: <Monitor className="h-8 w-8" />,
  "school-bus": <Bus className="h-8 w-8" />,
  uniform: <Shirt className="h-8 w-8" />,
  printing: <Printer className="h-8 w-8" />,
  cleaning: <Sparkles className="h-8 w-8" />,
  "professional-services": <Briefcase className="h-8 w-8" />,
  elevator: <ArrowUpDown className="h-8 w-8" />,
  security: <Shield className="h-8 w-8" />,
  gardening: <Leaf className="h-8 w-8" />,
  others: <MoreHorizontal className="h-8 w-8" />,
};

function daysAgo(isoDate?: string): string {
  if (!isoDate) return "";
  const diff = Math.floor((Date.now() - new Date(isoDate).getTime()) / 86400000);
  if (diff === 0) return "今天";
  if (diff === 1) return "1 天前";
  return `${diff} 天前`;
}

export function TenderCard({ tender, isCompact = false, isUrgent = false }: { tender: any; isCompact?: boolean; isUrgent?: boolean }) {
  const isOpen = tender.status === "open";
  const published = daysAgo(tender.createdAt);
  return (
    <Link href={`/tenders/${tenderSlug(tender.id, tender.title ?? "")}`}>
      <Card className={`hover:shadow-md transition-all duration-200 cursor-pointer group h-full ${isUrgent ? "hover:border-amber-400/40 border-amber-200/60 hover:bg-amber-50/40" : "hover:border-primary/30 hover:bg-primary/[0.03]"}`} data-testid={`tender-card-${tender.id}`}>
        <CardContent className={`flex flex-col justify-between h-full ${isCompact ? "p-4" : "p-5"}`}>
          <h3 className="font-semibold text-base leading-snug group-hover:text-primary transition-colors line-clamp-3 mb-3">{tender.title}</h3>
          <div className="flex flex-wrap items-center gap-1.5 mt-auto">
            {isUrgent && isOpen ? (
              <Badge className="whitespace-nowrap text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-300 hover:bg-amber-50">
                招標中
              </Badge>
            ) : isOpen ? (
              <Badge className="whitespace-nowrap inline-flex items-center rounded-md border px-2.5 py-0.5 font-semibold transition-colors hover-elevate shadow-xs text-xs text-[#2a6f4f] border-[#2a6f4f] bg-[#95ff0080]">
                招標中
              </Badge>
            ) : (
              <Badge className="whitespace-nowrap text-xs font-semibold bg-red-50 text-red-600 border border-red-300 hover:bg-red-50">
                已截止
              </Badge>
            )}
            {(tender.categories ?? []).map((cat: any) => (
              <Badge key={cat.slug} variant="outline" className="text-xs font-normal bg-[#2a6f4f26] border-[#2a6f4f26]">{cat.nameChinese}</Badge>
            ))}
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <MapPin className="h-3 w-3" />{tender.district}
            </span>
            {published && (
              <span className="text-xs text-muted-foreground/70 flex items-center gap-1">
                {published}發布
              </span>
            )}
            <span className={`text-xs font-medium flex items-center gap-1 ml-auto ${isUrgent ? "text-amber-600" : !isOpen ? "text-red-500" : "text-[#677e73]"}`}>
              截止 {tender.deadlineDate}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function HomePage() {
  const { isSignedIn, isLoaded } = useUser();
  const signedOut = !isLoaded || !isSignedIn;
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const recentRef = useRef<HTMLDivElement>(null);
  const todayRef = useRef<HTMLDivElement>(null);

  const heroLines = [
    "每日更新全港中學小學最新招標及投標資訊，為餐飲、工程、IT、清潔等各行業提供最快最準確的學校採購資訊。",
    "每日幫你搜尋、整理集合全港 1,100+ 間中小學招標資料。",
    "助您快速掌握最新商機。",
  ];
  const [heroIdx, setHeroIdx] = useState(0);
  const [heroFade, setHeroFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setHeroFade(false);
      setTimeout(() => {
        setHeroIdx(i => (i + 1) % heroLines.length);
        setHeroFade(true);
      }, 400);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if ((selectedCategory || selectedDistricts.length > 0) && resultsRef.current) {
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [selectedCategory, selectedDistricts]);

  const districtParam = selectedDistricts.length > 0 ? selectedDistricts.join(",") : undefined;
  const { data: stats } = useGetTenderStats();
  const { data: categories, isLoading: catLoading } = useListCategories();
  const { data: recentTenders, isLoading: recentLoading } = useGetRecentTenders({ limit: 20 }, {
    query: { queryKey: ["recentTenders", 20] }
  });
  const { data: closingSoon, isLoading: closingLoading } = useGetClosingSoonTenders();
  const { data: todayTenders, isLoading: todayLoading } = useGetTodayTenders();

  useEffect(() => {
    if (todayLoading || recentLoading) return;
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [todayLoading, recentLoading]);

  const { data: filteredTenders, isLoading: filteredLoading } = useListTenders(
    { category: selectedCategory ?? undefined, district: districtParam, limit: 20, status: "open" },
    { query: { queryKey: ["tenders", selectedCategory, districtParam, 20, "open"], enabled: !!(selectedCategory || districtParam) } }
  );

  function toggleDistrict(d: string) {
    setSelectedDistricts(prev =>
      prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={PUBLIC_PLATFORM_INTRO.pageTitle}
        description={PUBLIC_PLATFORM_INTRO.metaDescription}
        keywords="香港學校招標,學校投標,學校tender,香港tender,書面報價,學校書面報價,報價邀請,學校膳食招標,學校IT招標,學校工程招標,學校保安招標,學校清潔招標,學校維修招標,學校冷氣招標,學校消防招標,學校印刷招標,學校制服招標,學校傢俬招標,學校設備招標,Hong Kong school tender,school quotation Hong Kong"
        canonical="/"
      />
      {/* Hero Section */}
      <section className="bg-primary text-primary-foreground py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Skoltend
            </h1>
            <p className="text-xl md:text-2xl font-medium text-primary-foreground/90 mb-2">
              {PUBLIC_PLATFORM_INTRO.pageTitle}
            </p>
            <p
              className="text-primary-foreground/75 text-base mb-8 max-w-xl min-h-[3rem] transition-opacity duration-300"
              style={{ opacity: heroFade ? 1 : 0 }}
            >
              {heroLines[heroIdx]}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link href="/tenders">
                <Button size="lg" variant="secondary" className="font-semibold transition-all hover:scale-[1.03] hover:shadow-md" data-testid="button-browse-tenders">全部招標</Button>
              </Link>
              {signedOut && (
                <Link href="/pricing">
                  <Button size="lg" variant="outline" className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 font-semibold">
                    了解付費計劃
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Stats Bar — always rendered to prevent CLS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 pt-12 border-t border-primary-foreground/20">
            <div
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              data-testid="stat-total-open"
              onClick={() => recentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            >
              <div className="p-3 rounded-lg bg-primary-foreground/10">
                <Activity className="h-8 w-8 text-primary-foreground/80" />
              </div>
              <div>
                <div className="text-3xl font-bold">{stats ? stats.totalOpen : <span className="opacity-40">—</span>}</div>
                <div className="text-primary-foreground/70 text-sm mt-0.5">招標中</div>
              </div>
            </div>
            <Link href="/tenders">
              <div className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity">
                <div className="p-3 rounded-lg bg-primary-foreground/10">
                  <FileText className="h-8 w-8 text-primary-foreground/80" />
                </div>
                <div>
                  <div className="text-3xl font-bold">{stats ? stats.totalOpen + stats.totalClosed : <span className="opacity-40">—</span>}</div>
                  <div className="text-primary-foreground/70 text-sm mt-0.5">所有招標</div>
                </div>
              </div>
            </Link>
            <Link href="/schools">
              <div className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity">
                <div className="p-3 rounded-lg bg-primary-foreground/10">
                  <School className="h-8 w-8 text-primary-foreground/80" />
                </div>
                <div>
                  <div className="text-3xl font-bold">{stats ? stats.totalSchools : <span className="opacity-40">—</span>}</div>
                  <div className="text-primary-foreground/70 text-sm mt-0.5">覆蓋學校</div>
                </div>
              </div>
            </Link>
            <div
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => todayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            >
              <div className="p-3 rounded-lg bg-primary-foreground/10">
                <RefreshCw className="h-8 w-8 text-primary-foreground/80" />
              </div>
              <div>
                <div className="text-3xl font-bold">{stats ? stats.todayUpdated : <span className="opacity-40">—</span>}</div>
                <div className="text-primary-foreground/70 text-sm mt-0.5">今日更新</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Categories Section */}
      <section className="py-10 border-b bg-secondary/30">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-lg font-semibold mb-5">按行業分類</h2>
          {catLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {(categories ?? []).map(cat => (
                <button
                  key={cat.slug}
                  onClick={() => setSelectedCategory(selectedCategory === cat.slug ? null : cat.slug)}
                  data-testid={`category-filter-${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-4 rounded-lg border text-center transition-all duration-150 hover:border-primary/40 hover:bg-accent border-border bg-card text-muted-foreground font-extrabold text-[16px] rounded-tl-[6px] rounded-tr-[6px] rounded-br-[6px] rounded-bl-[6px]"
                >
                  <div className={selectedCategory === cat.slug ? "text-primary" : "text-muted-foreground"}>
                    {CATEGORY_ICONS[cat.slug] ?? <FileText className="h-8 w-8" />}
                  </div>
                  <span className="text-[16px] font-bold text-[#677e73] bg-[transparent]">{cat.nameChinese}</span>
                  <span className={`text-xs font-semibold ${selectedCategory === cat.slug ? "text-primary" : "text-foreground"}`}>
                    {cat.openCount} 招標中
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
      {/* District Filter */}
      <section className="py-6 border-b bg-background">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-start gap-3 flex-wrap">
            <span className="text-sm font-medium text-muted-foreground shrink-0 pt-1.5">地區篩選：</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedDistricts([])}
                data-testid="district-filter-全港"
                className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                  selectedDistricts.length === 0
                    ? "bg-primary text-primary-foreground border-primary font-medium"
                    : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                }`}
              >
                全港
              </button>
              {DISTRICTS.filter(d => d !== "全港").map(d => (
                <button
                  key={d}
                  onClick={() => toggleDistrict(d)}
                  data-testid={`district-filter-${d}`}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                    selectedDistricts.includes(d)
                      ? "bg-primary text-primary-foreground border-primary font-medium"
                      : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          {selectedDistricts.length > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-muted-foreground">已選：{selectedDistricts.join("、")}</span>
              <button onClick={() => setSelectedDistricts([])} className="text-xs text-primary hover:underline">清除</button>
            </div>
          )}
        </div>
      </section>
      {/* Filtered Results (if any filter active) */}
      {(selectedCategory || selectedDistricts.length > 0) && (
        <section ref={resultsRef} className="py-8 bg-secondary/20">
          <div className="container mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                篩選結果
                {filteredTenders && <span className="text-muted-foreground font-normal text-sm ml-2">({filteredTenders.total} 項)</span>}
              </h2>
              <Link href={`/tenders?${selectedCategory ? `category=${selectedCategory}&` : ""}${districtParam ? `district=${districtParam}` : ""}`}>
                <Button variant="ghost" size="sm" className="text-primary">查看全部 <ChevronRight className="h-4 w-4 ml-1" /></Button>
              </Link>
            </div>
            {filteredLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {[...(filteredTenders?.tenders ?? [])].sort((a, b) => (a.deadlineDate ?? "").localeCompare(b.deadlineDate ?? "")).map(t => <TenderCard key={t.id} tender={t} isCompact />)}
                {(!filteredTenders?.tenders.length) && <p className="text-muted-foreground text-sm col-span-3">未找到相關招標項目</p>}
              </div>
            )}
          </div>
        </section>
      )}
      <hr className="border-border" />
      {/* Today's Tenders - only shown when there are entries */}
      {(todayLoading || (todayTenders && todayTenders.filter(t => t.status === "open").length > 0)) && (
        <div ref={todayRef} id="today" className="container mx-auto px-4 md:px-8 pt-10 pb-10">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-emerald-600" />
              今日更新
              {todayTenders && (
                <span className="text-sm font-normal text-muted-foreground">({todayTenders.filter(t => t.status === "open").length} 項)</span>
              )}
            </h2>
            <Link href="/tenders">
              <Button variant="ghost" size="sm" className="text-primary text-sm">
                全部招標 <ChevronRight className="h-4 w-4 ml-0.5" />
              </Button>
            </Link>
          </div>
          {todayLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-lg" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {(todayTenders ?? []).filter(t => t.status === "open").map(t => <TenderCard key={t.id} tender={t} />)}
            </div>
          )}
        </div>
      )}
      <hr className="border-border" />
      {/* Recent Open Tenders - full width, 1 col mobile / 3 col desktop */}
      <div ref={recentRef} id="open" className="container mx-auto px-4 md:px-8 py-10">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            最新招標
          </h2>
          <Link href="/tenders">
            <Button variant="ghost" size="sm" className="text-primary text-sm">
              全部招標 <ChevronRight className="h-4 w-4 ml-0.5" />
            </Button>
          </Link>
        </div>
        {recentLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-lg" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {[...(recentTenders ?? [])].filter(t => t.status === "open").sort((a, b) => (a.deadlineDate ?? "").localeCompare(b.deadlineDate ?? "")).map(t => <TenderCard key={t.id} tender={t} />)}
          </div>
        )}
      </div>
      <hr className="border-border" />
      {/* Closing Soon */}
      <div className="container mx-auto px-4 md:px-8 pt-10 pb-6">
        <div className="flex items-center gap-2 mb-5">
          <Clock className="h-5 w-5 text-amber-600" />
          <h2 className="text-xl font-bold">即將截止</h2>
          {closingSoon && closingSoon.length > 0 && (
            <span className="text-sm font-normal text-muted-foreground">({closingSoon.length} 項)</span>
          )}
        </div>
        {closingLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
          </div>
        ) : closingSoon && closingSoon.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {closingSoon.slice(0, 20).map(t => (
              <div key={t.id} data-testid={`closing-soon-${t.id}`}>
                <TenderCard tender={t} isCompact isUrgent />
              </div>
            ))}
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="p-6 text-center text-muted-foreground text-sm">
              本週暫無即將截止招標
            </CardContent>
          </Card>
        )}
      </div>

      <section aria-labelledby="platform-introduction" className="border-t bg-secondary/20">
        <div className="container mx-auto px-4 md:px-8 py-10">
          <div className="max-w-3xl space-y-4">
            <h2 id="platform-introduction" className="text-xl md:text-2xl font-bold">{PUBLIC_PLATFORM_INTRO.heading}</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">{PUBLIC_PLATFORM_INTRO.description}</p>
            <p className="text-sm leading-relaxed text-muted-foreground">{PUBLIC_PLATFORM_INTRO.disclaimer}</p>
            <nav aria-label="平台介紹相關連結" className="flex flex-wrap gap-x-6 gap-y-2 pt-1">
              {PUBLIC_PLATFORM_INTRO.links.map(link => (
                <Link key={link.path} href={link.path} className="text-sm font-medium text-primary underline underline-offset-4 hover:opacity-80">
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </section>

      {/* Social Follow CTA */}
      <div className="border-t bg-secondary/30">
        <div className="container mx-auto px-4 md:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-foreground">追蹤 Skoltend，每日獲取最新招標資訊</p>
              <p className="text-xs text-muted-foreground mt-0.5">Follow us for daily tender alerts</p>
            </div>
            <div className="flex items-center gap-3">
              {SOCIAL_LINKS.map(({ href, label, icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card hover:border-primary/30 hover:bg-accent transition-all text-muted-foreground hover:text-foreground"
                >
                  {icon}
                  <span className="text-xs font-medium hidden sm:inline">{label}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Join CTA */}
      {signedOut && (
        <div className="container mx-auto px-4 md:px-8 pb-10">
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-5">
              <h3 className="font-semibold text-sm mb-2">付費會員專屬功能</h3>
              <ul className="text-xs text-muted-foreground space-y-1.5 mb-4">
                <li>· 查看學校名稱及聯絡方式</li>
                <li>· 每日電郵通知最新招標</li>
                <li>· Telegram 群組</li>
              </ul>
              <Link href="/pricing">
                <Button size="sm" className="w-full">了解付費計劃</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
